import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Container,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddNewSessionDialog from "app/components/common/Dialogs/school/AddNewSessionDialog";
import OrderDetailDialog from "app/components/common/Dialogs/school/OrderDetailDialog";
import RazorPayPaymentDialog from "app/components/common/Dialogs/school/RazorPayPaymentDialog";
import useRazorPayPaymentGate from "app/components/common/Dialogs/school/RazorPayPaymentGate";
import TransactionDetailDialog from "app/components/common/Dialogs/school/TransactionDetail";
import { getAllSessions } from "app/services/management";
import { getAllSessionOrderBySchool } from "app/services/orgService/school.service";
import { IndianCurrency, MenuProps } from "app/utils/helper";
import { CustomSelectBox } from "assets/styles/globalStyledComponent";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  selectActiveSession,
  selectSelectedSchool,
  selectSelectedSchoolId,
} from "../../selectors";

const Transaction = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:768px)");
  const activeSession = useSelector(selectActiveSession);
  const schoolDetails = useSelector(selectSelectedSchool);
  const selectedSchoolId = useSelector(selectSelectedSchoolId);

  const [state, setState] = useState({
    addSessionDialog: false,
    session: "",
    data: [],
    summary: {},
    orderDetailDialog: false,
    orderDetailData: {},
    transactionDetailDialog: false,
    collectPaymentDialog: false,
    sessionOrderDocId: "",
    transactionList: [],
    purchaseOrderDetails: {},
  });

  const calculateTobePaid = () => {
    const filteredList = state.data?.filter((item) => item?.checked);
    return filteredList.reduce((total, currentValue) => {
      const tobePaid =
        Number(currentValue?.netOrderAmount ?? 0) -
        Number(currentValue?.paidAmount ?? 0) -
        Number(currentValue?.totalExtraDiscount ?? 0);
      return total + tobePaid;
    }, 0);
  };

  const {
    isLoading: getAllSessionOrderBySchoolLoading,
    isFetching: getAllSessionOrderBySchoolFetching,
    refetch: getAllSessionOrderBySchoolRefetch,
  } = useQuery({
    queryKey: ["getAllSessionOrderBySchool", state.session],
    queryFn: () => getAllSessionOrderBySchool({ session: state.session }),
    onSuccess: (success) => {
      setState((prev) => ({
        ...prev,
        data: success?.data?.list,
        summary: success?.data?.summary,
      }));
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    },
    enabled: !!state.session,
  });

  const { data: sessionsResponse } = useQuery({
    queryKey: ["getAllSessions"],
    queryFn: () => getAllSessions(),
    onError: (error) => {
      toast.error(error?.response?.data?.message);
    },
  });

  useEffect(() => {
    setState((prev) => ({ ...prev, session: activeSession }));
  }, [activeSession]);

  const isAnyItemChecked = () => {
    return state.data.some((item) => item.checked);
  };

  const { openRazorpay } = useRazorPayPaymentGate({
    refetch: getAllSessionOrderBySchoolRefetch,
  });

  // Helper to get status color
  const getStatusColor = (status) => {
    if (status === "Paid") return "success";
    if (status === "REJECTED" || status === "Not Paid") return "error";
    return "warning";
  };

  return (
    <>
      <Helmet>
        <title>Transaction History</title>
      </Helmet>

      {/* Dialogs */}
      <RazorPayPaymentDialog
        open={state.collectPaymentDialog}
        onClose={() =>
          setState((prev) => ({ ...prev, collectPaymentDialog: false }))
        }
        data={state.data?.filter((item) => item?.checked) || []}
        schoolDocId={schoolDetails?._id}
        session={state.session}
        refetch={() => getAllSessionOrderBySchoolRefetch()}
      />

      <TransactionDetailDialog
        open={state.transactionDetailDialog}
        onClose={() =>
          setState((prev) => ({ ...prev, transactionDetailDialog: false }))
        }
        id={state.sessionOrderDocId}
        transactionList={state.transactionList}
      />

      <OrderDetailDialog
        open={state.orderDetailDialog}
        onClose={() =>
          setState((prev) => ({
            ...prev,
            orderDetailDialog: false,
            orderDetailData: {},
          }))
        }
        data={state.orderDetailData}
      />

      <AddNewSessionDialog
        open={state.addSessionDialog}
        onClose={() =>
          setState((prev) => ({ ...prev, addSessionDialog: false }))
        }
        schoolDocId={selectedSchoolId}
        handlePayementGateWay={(sucessData) => {
          openRazorpay(sucessData);
        }}
      />

      <Box>
        {/* Header Section */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={2}
          mb={2}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            flexWrap="wrap"
          >
            <CustomSelectBox
              MenuProps={MenuProps}
              size="medium"
              sx={{ minWidth: 200, borderRadius: 2 }}
              displayEmpty
              value={state.session}
            >
              <MenuItem value="" disabled>
                Select Session
              </MenuItem>
              {sessionsResponse?.data?.list?.map((item, idx) => (
                <MenuItem
                  value={item?.sessionName}
                  key={idx}
                  onClick={() =>
                    setState((prev) => ({
                      ...prev,
                      session: item?.sessionName,
                    }))
                  }
                >
                  {item?.sessionName} (Limit: {item?.studentLimitCount})
                </MenuItem>
              ))}
            </CustomSelectBox>

            <LoadingButton
              loading={
                getAllSessionOrderBySchoolLoading ||
                getAllSessionOrderBySchoolFetching
              }
              variant="contained"
              color="primary"
              onClick={() => getAllSessionOrderBySchoolRefetch()}
              disabled={!state.session}
              sx={{ borderRadius: 2, px: 3 }}
            >
              View
            </LoadingButton>

            <Typography
              variant="body1"
              sx={{
                display: { xs: "none", sm: "block" },
                color: "text.secondary",
                fontSize: "1rem",
              }}
            >
              Student Limit:{" "}
              <span
                style={{ color: theme.palette.error.main, fontWeight: "bold" }}
              >
                {sessionsResponse?.data?.list?.find(
                  (item) => item?.sessionName === state.session
                )?.studentLimitCount || "--"}
              </span>
            </Typography>
          </Stack>

          <Button
            variant="outlined"
            color="primary"
            onClick={() =>
              setState((prev) => ({ ...prev, addSessionDialog: true }))
            }
            sx={{ whiteSpace: "nowrap", borderRadius: 2, px: 3 }}
          >
            + New Session
          </Button>
        </Stack>

        {/* Info Cards */}
        <Grid container spacing={1} mb={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent sx={{ py: 1 }}>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontWeight={500}
                  gutterBottom
                >
                  Verification Status
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: schoolDetails?.verified
                      ? "success.main"
                      : "error.main",
                    mt: 0.5,
                    fontWeight: 600,
                  }}
                >
                  {schoolDetails?.verified ? "Verified" : "Not Verified" ?? "--"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent sx={{ py: 1 }}>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontWeight={500}
                  gutterBottom
                >
                  Account Status
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color:
                      schoolDetails?.accountStatus === "ACTIVE"
                        ? "success.main"
                        : "error.main",
                    mt: 0.5,
                    fontWeight: 600,
                  }}
                >
                  {schoolDetails?.accountStatus ?? "--"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <Card variant="outlined" sx={{ borderRadius: 3 }}>
              <CardContent sx={{ py: 1 }}>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontWeight={500}
                  gutterBottom
                >
                  Subscription Type
                </Typography>
                <Typography
                  variant="h5"
                  color="primary"
                  sx={{ mt: 0.5, fontWeight: 600 }}
                >
                  {schoolDetails?.subscriptionType ?? "--"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Table Section */}
        {state.data?.length > 0 && (
          <>
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ mb: 2, borderRadius: 3 }}
            >
              <Table sx={{ minWidth: 850 }} size="medium">
                <TableHead sx={{ bgcolor: "grey.100" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                      Product Description
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                      Due Date
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                      Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                      Discount
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                      Paid
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: "bold", fontSize: "1rem" }}
                      align="center"
                    >
                      To Be Paid
                    </TableCell>
                    <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {state.data.map((item, i) => (
                    <TableRow key={i} hover>
                      <TableCell>
                        <Typography
                          variant="body1"
                          color="primary"
                          sx={{
                            cursor: "pointer",
                            fontWeight: 500,
                            fontSize: "1.15rem",
                          }}
                          onClick={() =>
                            setState((prev) => ({
                              ...prev,
                              orderDetailData: item,
                              orderDetailDialog: true,
                            }))
                          }
                        >
                          {item?.productDetail?.productType} -{" "}
                          {item?.productDetail?.productName}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontSize: "1.15rem" }}
                        >
                          {item?.productDetail?.productDescription}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: "1.15rem" }}>
                        {dayjs(item?.dueDate).format("DD MMM, YYYY")}
                      </TableCell>
                      <TableCell sx={{ fontSize: "1.15rem" }}>
                        {IndianCurrency(item?.orderAmount)}
                      </TableCell>
                      <TableCell sx={{ fontSize: "1.15rem" }}>
                        {IndianCurrency(
                          Number(item?.discountAtpurchage ?? 0) +
                            Number(item?.totalExtraDiscount ?? 0)
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body1"
                          color="primary"
                          sx={{
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontSize: "1.15rem",
                          }}
                          onClick={() =>
                            setState((prev) => ({
                              ...prev,
                              sessionOrderDocId: item?._id,
                              transactionDetailDialog: true,
                              transactionList: item?.transactions,
                            }))
                          }
                        >
                          {IndianCurrency(Number(item?.paidAmount ?? 0))}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="center"
                          spacing={1}
                        >
                          {Number(item?.netOrderAmount ?? 0) -
                            Number(item?.paidAmount ?? 0) -
                            Number(item?.totalExtraDiscount ?? 0) !==
                            0 &&
                            !["Paid"].includes(item?.status) && (
                              <Checkbox
                                checked={!!item?.checked}
                                size="small"
                                onChange={() => {
                                  item.checked = !item.checked;
                                  setState((prev) => ({ ...prev }));
                                }}
                              />
                            )}
                          <Typography
                            variant="body1"
                            fontWeight="600"
                            sx={{ fontSize: "1.15rem" }}
                          >
                            {IndianCurrency(
                              Number(item?.netOrderAmount ?? 0) -
                                Number(item?.paidAmount ?? 0) -
                                Number(item?.totalExtraDiscount ?? 0)
                            )}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item?.status}
                          color={getStatusColor(item?.status)}
                          size="medium"
                          variant="outlined"
                          sx={{ borderRadius: 2, fontWeight: 500 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Summary & Actions */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: "primary.50",
                borderRadius: 3,
                marginBottom: isMobile ? 10 : 0,
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
              >
                <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "0.875rem", mb: 0.5 }}
                    >
                      Total Order
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {IndianCurrency(state?.summary?.t_orderAmount)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "0.875rem", mb: 0.5 }}
                    >
                      Discount
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {IndianCurrency(
                        Number(state?.summary?.t_discountAtpurchage ?? 0) +
                          Number(state?.summary?.t_extraDiscountAmount ?? 0)
                      )}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "0.875rem", mb: 0.5 }}
                    >
                      Paid
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color="success.main"
                    >
                      {IndianCurrency(state?.summary?.t_taxableAmount)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "0.875rem", mb: 0.5 }}
                    >
                      Balance
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      color="error.main"
                    >
                      {IndianCurrency(state?.summary?.t_balanceAmount)}
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={2}>
                  <Typography
                    variant="body1"
                    fontWeight="600"
                    color="primary"
                    sx={{ fontSize: "1.15rem" }}
                  >
                    Selected: {IndianCurrency(calculateTobePaid())}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!isAnyItemChecked()}
                    onClick={() =>
                      setState((prev) => ({
                        ...prev,
                        collectPaymentDialog: true,
                      }))
                    }
                    sx={{ borderRadius: 2, px: 2, py: 1 }}
                  >
                    Pay Now
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </>
        )}
      </Box>
    </>
  );
};

export default Transaction;
